import { useAuthStore } from '@/stores/authStore';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;
  const token = useAuthStore.getState().token;

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, config);

  if (!response.ok) {
    if (response.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }

    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: 'Unknown error' };
    }

    throw new ApiError(errorData.error || 'Request failed', response.status, errorData.details);
  }

  return response.json();
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),
  post: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'POST', body }),
  patch: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'PATCH', body }),
  put: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'PUT', body }),
  delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
};

// Auth API
export const authApi = {
  verifyMagicLink: (token: string) =>
    api.post<{
      token: string;
      admin: {
        id: number;
        telegram_user_id: string;
        telegram_username: string;
        role: string;
        notify_channels: string[];
      };
    }>('/auth/verify-magic-link', { token }),
  me: () =>
    api.get<{
      admin: {
        id: number;
        telegram_user_id: string;
        telegram_username: string;
        role: string;
        notify_channels: string[];
      };
    }>('/auth/me'),
  logout: () => api.post<{ success: boolean }>('/auth/logout'),
};

// Submissions API
export interface Submission {
  id: number;
  ticket_id: string;
  user_id: number;
  telegram_username: string | null;
  telegram_user_id: string;
  channel: string;
  type: string;
  status: string;
  priority: string;
  resolution: string | null;
  os_version: string | null;
  app_version: string | null;
  content: string;
  screenshot_key: string | null;
  admin_comment: string | null;
  reviewed_by: number | null;
  ai_flags: string;
  ai_duplicate_suggestion: string | null;
  github_issue_url: string | null;
  github_issue_number: number | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export interface SubmissionsResponse {
  submissions: Submission[];
  total: number;
}

export interface SubmissionFilters {
  status?: string;
  channel?: string;
  type?: string;
  priority?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const submissionsApi = {
  list: (filters?: SubmissionFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const query = params.toString();
    return api.get<SubmissionsResponse>(`/submissions${query ? `?${query}` : ''}`);
  },
  get: (ticketId: string) => api.get<{ submission: Submission }>(`/submissions/${ticketId}`),
  update: (ticketId: string, data: Partial<Submission>) =>
    api.patch<{ submission: Submission }>(`/submissions/${ticketId}`, data),
  createGitHubIssue: (ticketId: string, data: { title: string; labels?: string[]; body?: string }) =>
    api.post<{ issue_url: string; issue_number: number }>(`/submissions/${ticketId}/github-issue`, data),
  fetchScreenshot: async (ticketId: string): Promise<string | null> => {
    const token = useAuthStore.getState().token;
    try {
      const response = await fetch(`${API_BASE}/submissions/${ticketId}/screenshot`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  },
};

// Bans API
export interface Ban {
  id: number;
  user_id: number;
  banned_by: number;
  reason: string | null;
  expires_at: string | null;
  permanent: number;
  active: number;
  created_at: string;
}

export interface BansResponse {
  bans: Ban[];
  total: number;
}

export const bansApi = {
  list: (filters?: { active?: boolean; page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, String(value));
        }
      });
    }
    const query = params.toString();
    return api.get<BansResponse>(`/bans${query ? `?${query}` : ''}`);
  },
  create: (data: { telegram_user_id: string; reason?: string; duration_days?: number }) =>
    api.post<{ ban: Ban }>('/bans', data),
  update: (id: number, data: { reason?: string; expires_at?: string | null; active?: boolean }) =>
    api.patch<{ ban: Ban }>(`/bans/${id}`, data),
  delete: (id: number) => api.delete<{ success: boolean }>(`/bans/${id}`),
};

// Appeals API
export interface Appeal {
  id: number;
  ban_id: number;
  message: string;
  status: string;
  admin_response: string | null;
  reviewed_by: number | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface AppealsResponse {
  appeals: Appeal[];
  total: number;
}

export const appealsApi = {
  list: (filters?: { status?: string; page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const query = params.toString();
    return api.get<AppealsResponse>(`/appeals${query ? `?${query}` : ''}`);
  },
  update: (id: number, data: { status: 'approved' | 'rejected'; admin_response?: string }) =>
    api.patch<{ appeal: Appeal }>(`/appeals/${id}`, data),
};

// Admins API
export interface Admin {
  id: number;
  telegram_user_id: string;
  telegram_username: string;
  role: string;
  notify_channels: string;
  created_at: string;
  created_by: number | null;
}

export interface AdminsResponse {
  admins: Admin[];
}

export const adminsApi = {
  list: () => api.get<AdminsResponse>('/admins'),
  create: (data: {
    telegram_user_id: string;
    telegram_username: string;
    role: string;
    notify_channels: string[];
  }) => api.post<{ admin: Admin }>('/admins', data),
  update: (id: number, data: { role?: string; notify_channels?: string[] }) =>
    api.patch<{ admin: Admin }>(`/admins/${id}`, data),
  delete: (id: number) => api.delete<{ success: boolean }>(`/admins/${id}`),
};

// Settings API
export interface Settings {
  global: {
    enabled: boolean;
    disabled_reason: string | null;
    greeting_message: string | null;
    daily_submission_limit: number;
  } | null;
  channels: Record<
    string,
    {
      enabled: boolean;
      channel: string | null;
      disabled_reason: string | null;
      greeting_message: string | null;
      daily_submission_limit: number;
    }
  >;
}

export const settingsApi = {
  get: () => api.get<Settings>('/settings'),
  update: (data: {
    channel?: string;
    enabled?: boolean;
    disabled_reason?: string;
    greeting_message?: string;
    daily_submission_limit?: number;
  }) => api.patch<Settings>('/settings', data),
};

// Stats API
export interface Stats {
  overview: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    resolved_today: number;
  };
  daily: Array<{ date: string; count: number }>;
  by_channel: Array<{ channel: string; count: number }>;
  top_submitters: Array<{ username: string; count: number }>;
}

export const statsApi = {
  get: () => api.get<Stats>('/stats'),
};

// Activity API
export interface ActivityLog {
  id: number;
  action: string;
  actor_type: string;
  actor_id: number | null;
  target_type: string | null;
  target_id: number | null;
  details: string | null;
  created_at: string;
}

export interface ActivityResponse {
  logs: ActivityLog[];
  total: number;
}

export const activityApi = {
  list: (filters?: { action?: string; actor_type?: string; page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const query = params.toString();
    return api.get<ActivityResponse>(`/activity${query ? `?${query}` : ''}`);
  },
};

// Knowledge Base API
export interface KBArticle {
  id: number;
  question: string;
  answer: string;
  keywords: string[];
  category: string | null;
  priority: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface KBArticlesResponse {
  data: KBArticle[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface KBFilters {
  category?: string;
  enabled?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export const knowledgeBaseApi = {
  list: (filters?: KBFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const query = params.toString();
    return api.get<KBArticlesResponse>(`/knowledge-base${query ? `?${query}` : ''}`);
  },
  get: (id: number) => api.get<KBArticle>(`/knowledge-base/${id}`),
  create: (data: Omit<KBArticle, 'id' | 'created_at' | 'updated_at'>) =>
    api.post<KBArticle>('/knowledge-base', data),
  update: (id: number, data: Partial<Omit<KBArticle, 'id' | 'created_at' | 'updated_at'>>) =>
    api.patch<KBArticle>(`/knowledge-base/${id}`, data),
  delete: (id: number) => api.delete<{ success: boolean }>(`/knowledge-base/${id}`),
  categories: () => api.get<{ categories: string[] }>('/knowledge-base/categories'),
};

// AI Config API
export interface AIConfig {
  id: number;
  system_prompt: string;
  off_topic_response: string | null;
  escalation_threshold: number;
  max_history_messages: number;
  enabled: boolean;
  updated_at: string;
  updated_by: number | null;
}

export const aiConfigApi = {
  get: () => api.get<AIConfig>('/ai-config'),
  update: (data: Partial<Omit<AIConfig, 'id' | 'updated_at' | 'updated_by'>>) =>
    api.patch<AIConfig>('/ai-config', data),
};
